import React from "react";
import { Referral } from "@/modules/leads/types";
import { GetLeadResponse } from "@/modules/leads/dtos";
import { Box, Divider, Typography } from "@mui/material";
import UserTag from "@/modules/user/components/user-tag";
import { Group } from "@/modules/lead-page/dtos/index-v2";
import InfoItem from "@/modules/common/components/input/info-item";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import { capitalizeWords } from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/utils";
// import ListingCard from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/components/listing-card";
// import { Listing } from "@/modules/listing-dashboard/dtos";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import {
  formatSaleInfo,
  formatSalePrice,
} from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/utils/index.ts";

interface ConfirmMergeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  selections: any;
  parentLeadData: GetLeadResponse | undefined;
  sortedLeadTypeGroups: Group[];
  selectedName: string;
  parentSales?: any[];
  childSales?: any[];
  hasPropertyTracks?: boolean;
}

const ConfirmMergeModal: React.FC<ConfirmMergeModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  selections,
  parentLeadData,
  sortedLeadTypeGroups,
  selectedName,
  parentSales,
  childSales,
  hasPropertyTracks,
}) => {
  const screen = useScreenSize();
  // const firstAcceptListingTypeUid = useMemo(() => {
  //   return sortedLeadTypeGroups.find((g) => g.type.acceptListing === true)?.type
  //     .uid;
  // }, [sortedLeadTypeGroups]);

  const hasSales =
    (parentSales && parentSales.length > 0) ||
    (childSales && childSales.length > 0);

  const ModalWrapper = screen.isMobile ? MobileModal : CommonModal;

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      isLoading={isLoading}
      title="Confirm Merge Leads"
      confirmButtonText="Confirm"
      cancelButtonText="Cancel"
      sx={
        !screen.isMobile ? { maxWidth: "600px", maxHeight: "750px" } : undefined
      }
    >
      <Box
        sx={{
          p: screen.isMobile ? 0 : 2,
          overflowY: "auto",
          maxHeight: screen.isMobile ? "100%" : "650px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h6">Customer Information</Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ flex: "1 1 45%" }}>
            <InfoItem label="Customer Name" value={selectedName} />
          </Box>
          <Box sx={{ flex: "1 1 45%" }}>
            <InfoItem
              label="Customer Email"
              value={parentLeadData?.data?.lead?.email}
            />
          </Box>

          <Box sx={{ flex: "1 1 45%" }}>
            <InfoItem
              label="Date of last request"
              value={parentLeadData?.data?.lead?.date_of_last_request}
            />
          </Box>
          <Box sx={{ flex: "1 1 45%" }}>
            <InfoItem
              label="Date of last contact"
              value={
                parentLeadData?.data?.lead?.date_of_last_contact ?? "Not set"
              }
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              fontWeight: 500,
              fontSize: "12px",
              letterSpacing: "0.5px",
            }}
          >
            Parent Customer Leads
          </Typography>
        </Divider>

        {sortedLeadTypeGroups.map((group) => {
          const { parent, child, type } = group;
          const leadSource = parent || child;
          if (!leadSource) return null;

          const isOnlyParent = parent && !child;
          const isOnlyChild = child && !parent;

          // const parentListings = parent?.listings || [];
          // const childListings = type.acceptListing ? child?.listings || [] : [];

          // const additionalChildListings =
          //   type.acceptListing && type.uid === firstAcceptListingTypeUid
          //     ? sortedLeadTypeGroups
          //         .filter(
          //           (g) =>
          //             g.type.uid !== type.uid &&
          //             g.child?.listings?.length &&
          //             g.child.leadType.acceptListing === false
          //         )
          //         .flatMap((g) => g.child!.listings)
          //     : [];

          // const allListingsToShow = [
          //   ...parentListings,
          //   ...childListings,
          //   ...additionalChildListings,
          // ];

          return (
            <Box key={type.uid}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                {`${capitalizeWords(type.name)} Details`}
                {isOnlyParent && " (Exists only on the parent customer)"}
                {isOnlyChild && " (Will be merged from child customer)"}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ flex: "1 1 45%" }}>
                  <InfoItem
                    label="Method of Contact"
                    value={
                      parent?.methodOfContact?.name?.toLowerCase() ||
                      child?.methodOfContact?.name?.toLowerCase() ||
                      "not set"
                    }
                  />
                </Box>

                <Box sx={{ flex: "1 1 45%" }}>
                  <InfoItem
                    label="Location"
                    value={
                      parent?.location?.name?.toLowerCase() ||
                      child?.location?.name?.toLowerCase() ||
                      "not set"
                    }
                  />
                </Box>

                <Box sx={{ flex: "1 1 45%" }}>
                  <InfoItem
                    label="Referral"
                    value={(() => {
                      const selectedUid = selections[type.uid]?.referral;

                      const getReferralName = (lead: any) => {
                        const ref = Array.isArray(lead?.referral)
                          ? lead?.referral.find((r: Referral) => !r.isExpired)
                          : !lead?.referral?.isExpired
                          ? lead?.referral
                          : undefined;
                        return ref?.referrer?.uid === selectedUid
                          ? ref?.referrer?.name?.toLowerCase()
                          : null;
                      };

                      return (
                        getReferralName(parent) ||
                        getReferralName(child) ||
                        "not set"
                      );
                    })()}
                  />
                </Box>

                <Box sx={{ flex: "1 1 45%" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Assigned User
                  </Typography>
                  {parent?.user?.name ? (
                    <UserTag
                      name={parent.user.name}
                      color={parent.user.color}
                    />
                  ) : (
                    <InfoItem label="" value="not set" />
                  )}
                </Box>
              </Box>

              {/* {allListingsToShow.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ mt: "16px", color: "text.secondary" }}
                  >
                    Listings
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {allListingsToShow
                        .filter(
                          (listing): listing is Listing => listing !== undefined
                        )
                        .map((listing) => (
                          <ListingCard key={listing.id} listing={listing} />
                        ))}
                    </Box>
                  </Box>
                </>
              )} */}

              <Divider sx={{ mt: "16px" }} />
            </Box>
          );
        })}

        {/* Sales Section */}
        {hasSales && (
          <>
            <Box
              key={`sales-section-${parentSales?.length}-${childSales?.length}`}
            >
              <Typography
                sx={{
                  ...nawyTheme.nawy.typography.english.variants.body.md
                    .semibold,
                  color: nawyTheme.nawy.colors.semantic.text.primary,
                  mb: nawyTheme.nawy.spacing.sm,
                }}
              >
                Sales
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                }}
              >
                {/* Combined Parent and Child Sales */}
                {[...(parentSales || []), ...(childSales || [])].length > 0 ? (
                  [...(parentSales || []), ...(childSales || [])].map(
                    (sale, index) => (
                      <Box key={index} sx={{ mb: 1 }}>
                        <Typography
                          sx={{
                            ...nawyTheme.nawy.typography.english.variants.body
                              .sm.semibold,
                            color: nawyTheme.nawy.colors.semantic.text.primary,
                            mb: nawyTheme.nawy.spacing.xs,
                          }}
                        >
                          {formatSaleInfo(sale)}
                        </Typography>
                        <Typography
                          sx={{
                            ...nawyTheme.nawy.typography.english.variants.body
                              .xs.regular,
                            color:
                              nawyTheme.nawy.colors.semantic.text.secondary,
                          }}
                        >
                          {formatSalePrice(sale?.unitValue || 0)}
                        </Typography>
                      </Box>
                    )
                  )
                ) : (
                  <Typography
                    sx={{
                      ...nawyTheme.nawy.typography.english.variants.body.sm
                        .regular,
                      color: nawyTheme.nawy.colors.semantic.text.tertiary,
                      fontStyle: "italic",
                    }}
                  >
                    No sales
                  </Typography>
                )}
              </Box>
            </Box>
            <Typography
              sx={{
                ...nawyTheme.nawy.typography.english.variants.body.sm.semibold,
                color: "#F79008",
              }}
            >
              Please note that all child lead sales will be moved to the parent
              lead once you merge.
            </Typography>
          </>
        )}

        {hasPropertyTracks && (
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.sm.semibold,
              color: "#F79008",
            }}
          >
            Property tracks from the child lead will be moved to the parent lead
            once you merge.
          </Typography>
        )}
      </Box>
    </ModalWrapper>
  );
};

export default ConfirmMergeModal;
